import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler } from '@angular/core';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// Import 3rd party components
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ModalModule } from 'ngx-bootstrap/modal';
import { AlertModule } from 'ngx-bootstrap/alert';
import { NgScrollbarModule } from 'ngx-scrollbar';

import { SidebarModule } from '@coreui/angular';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { BaseLayoutComponent } from './modules/base-layout';

const APP_CONTAINERS = [BaseLayoutComponent];
import { P404Component } from './modules/error/404.component';
import { P500Component } from './modules/error/500.component';

// Import 3rd party components
import { environment } from '@env/environment';
import { Effects, ROOT_REDUCER } from './store/main';
import { BlockFacilitiesComponent } from './modules/block-facilities/block-facilities.component';
import { StatementsComponent } from './modules/block-facilities/statements/statements.component';
import { CoreDetailChangesComponent } from './modules/block-facilities/core-detail-changes/core-detail-changes.component';
import {
  RunManagementServiceToken,
  RunManagementService,
} from './services/run-management/main';
import { WebAdminAPI, WebAdminAPIToken } from './api/web-admin/main';
import {
  AuthServiceToken,
  WebAdminAuthService,
} from './services/auth/web-admin';
import { UsersService, UsersServiceToken } from './services/users/main';
import { httpInterceptorProviders } from './services/http-interceptors';
import { StorageServiceToken } from './services/storage/interface';
import { FSServiceToken } from './services/fs/interface';
import { FileSystemService } from './services/fs/fs';
import { LocationServiceToken } from './services/location/interface';
import { LoginComponent } from './modules/login/login.component';
import { AlertMessageComponent } from '@sharedAngular/components/app-alert/alert.component';
import { MessageService } from '@sharedServices/app-messages.service';
import { RunService } from '@sharedServices/run.service';
import { AuditTrailService } from '@sharedServices/audit-trail.service';
import { XLSXService } from '@sharedServices/xlsx.service';
import { SharedModule } from './modules/shared.module';
import { DatesService } from '@sharedServices/dates.service';
import { GlobalErrorHandler } from '@sharedServices/interceptors/global-error-handler';

import * as Sentry from '@sentry/browser';
import { FunderAllocationService } from '@sharedAngular/services/admin-trustee/funder-allocation.service';

if (environment.production) {
  Sentry.init({
    dsn: 'https://704542a254c042e99e8c73c58693f58b@sentry.io/1861105',
    environment: environment.environmentName,
    release: 'tmnz-ui-trustee@1.21.6',
    beforeSend(event, hint) {
      /* tslint:disable:no-string-literal only-arrow-functions */
      const isNonErrorException =
        event.exception.values[0].value.startsWith(
          'Non-Error exception captured',
        ) ||
        hint.originalException['message']?.startsWith(
          'Non-Error exception captured',
        );
      /* tslint:enable:no-string-literal only-arrow-functions */

      if (isNonErrorException) {
        // We want to ignore those kind of errors
        return null;
      }
      return event;
    },
  });
}

@NgModule({
  exports: [ModalModule, AlertModule],
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    LoginComponent,
    P404Component,
    P500Component,
    BlockFacilitiesComponent,
    StatementsComponent,
    CoreDetailChangesComponent,
    AlertMessageComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    SharedModule.forRoot(),
    BrowserModule,
    AppRoutingModule,
    SidebarModule,
    AlertModule.forRoot(),
    NgScrollbarModule,
    BsDropdownModule.forRoot(),
    BrowserAnimationsModule,
    TabsModule.forRoot(),
    ModalModule.forRoot(),
    ReactiveFormsModule,
    FormsModule,
    StoreModule.forRoot(ROOT_REDUCER),
    EffectsModule.forRoot(Effects),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
  ],
  providers: [
    {
      provide: LocationServiceToken,
      useFactory: () => window.location,
    },
    {
      provide: StorageServiceToken,
      useFactory: () => localStorage,
    },
    {
      provide: FSServiceToken,
      useClass: FileSystemService,
    },
    {
      provide: AuthServiceToken,
      useClass: WebAdminAuthService,
    },
    {
      provide: WebAdminAPIToken,
      useClass: WebAdminAPI,
    },
    {
      provide: UsersServiceToken,
      useClass: UsersService,
    },
    {
      provide: RunManagementServiceToken,
      useClass: RunManagementService,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    { provide: 'project', useValue: 'TPORTAL' },
    httpInterceptorProviders,
    MessageService,
    FunderAllocationService,
    RunService,
    AuditTrailService,
    XLSXService,
    DatesService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  // Diagnostic only: inspect router configuration
  constructor(router: Router) {
    // Use a custom replacer to display function names in the route configs
    // const replacer = (key, value) => (typeof value === 'function') ? value.name : value;
  }
}
